import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { authColors } from "../../../styles/authTokens";
import { ChevronDownIcon } from "../../atoms/ChevronDownIcon";
import { WorkspaceInitialsBadge } from "../../atoms/WorkspaceInitialsBadge";
import { TenantSelectList } from "../TenantSelectList";
import {
  Wrap,
  Trigger,
  TriggerBody,
  TriggerLabel,
  Chevron,
  MenuPanel,
} from "./TenantSelectDropdown.styles";
import type { TenantSelectDropdownProps } from "./TenantSelectDropdown.types";

/**
 * Closed-by-default picker for the login tenant-selection step.
 *
 * The trigger is a textbox-like control that shows the chosen tenant; opening
 * it reveals the same scrollable `TenantSelectList` used before this was a
 * dropdown, reused unchanged so its loading/error/empty states don't need
 * re-implementing here. Owns its own open/closed state, matching the existing
 * `WorkspaceInviteRoleSelect` dropdown molecule's pattern.
 */
export const TenantSelectDropdown: React.FC<TenantSelectDropdownProps> = ({
  tenants,
  selectedTenantId,
  onSelect,
  isLoading = false,
  errorMessage = null,
  onRetry,
  disabled = false,
  placeholder = "Choose an organization",
  className,
}) => {
  const [open, setOpen] = useState(!!errorMessage);
  const close = useCallback(() => setOpen(false), []);

  // A load failure must stay visible without an extra click — the closed
  // trigger has no room to show it, so surface it the same way the flat list
  // used to (always on screen) by opening onto it automatically. Loading and
  // empty state is left alone here — but note the guard on this branch routes
  // a user with NO tenants straight to this step, so the empty case is
  // reachable behind a closed trigger. Flagged, not silently changed.
  useEffect(() => {
    if (errorMessage) setOpen(true);
  }, [errorMessage]);

  const { triggerRef, menuRef, rect } = usePortalMenu<
    HTMLButtonElement,
    HTMLDivElement
  >({ open, onOutsideClick: close });

  const selectedTenant =
    tenants.find((t) => t.tenant_id === selectedTenantId) ?? null;

  // Selecting closes the menu — the trigger becomes the visible record of the
  // choice, so there is no reason to leave the list open after a pick.
  const handleSelect = useCallback(
    (tenantId: string) => {
      onSelect(tenantId);
      close();
    },
    [onSelect, close],
  );

  return (
    <Wrap className={className}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select organization"
        disabled={disabled}
        $open={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <TriggerBody>
          {selectedTenant && (
            <WorkspaceInitialsBadge name={selectedTenant.tenant_name} ariaLabel="" />
          )}
          <TriggerLabel $isPlaceholder={!selectedTenant}>
            {selectedTenant?.tenant_name ?? placeholder}
          </TriggerLabel>
        </TriggerBody>
        <Chevron $open={open}>
          <ChevronDownIcon size={20} color={authColors.textMedEmphasis} />
        </Chevron>
      </Trigger>

      {open &&
        rect &&
        createPortal(
          <MenuPanel
            ref={menuRef}
            style={{
              ...(rect.bottom != null
                ? { bottom: rect.bottom, top: "auto" }
                : { top: rect.top, bottom: "auto" }),
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
            }}
          >
            <TenantSelectList
              tenants={tenants}
              selectedTenantId={selectedTenantId}
              onSelect={handleSelect}
              isLoading={isLoading}
              errorMessage={errorMessage}
              onRetry={onRetry}
              disabled={disabled}
            />
          </MenuPanel>,
          document.body,
        )}
    </Wrap>
  );
};

export default TenantSelectDropdown;
